import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import * as yup from "yup";

import { GET_LIST, UPDATE, useClient } from "@peracto/client";
import {
    Form,
    Group,
    Heading,
    Input,
    LoadingIcon,
} from "@peracto/peracto-ui";

import { formatDataForAPI, formatDataForForm } from "../utils";
import { useBabbageAccountConfiguration } from "../useBabbageAccountConfiguration";
import BabbageAccountConfigurationFields from "../BabbageAccountConfigurationFields";

const LogoSync = ({ setCustomLogo }) => {
    const { values } = useFormikContext();
    useEffect(() => {
        setCustomLogo(values.customLogo);
    }, [values.customLogo, setCustomLogo]);
    return null;
};

const schema = yup.object().shape({
    mainAccountNumber: yup.string().required(),
});

export const AccountsList = () => {
    const { client } = useClient();

    const [loading, setLoading] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const [babbageAccounts, setBabbageAccounts] = useState([]);
    const [mainAccountNumber, setMainAccountNumber] = useState("");

    const config = useBabbageAccountConfiguration(babbageAccounts);

    const {
        categoriesLoading,
        t3CategoriesLoading,
        brandsLoading,
        categoryOptions,
        setSelectedCategory,
        setRestrictedBrands,
        setPrioritisedBrands,
    } = config;

    // Reset category/brand state when the looked-up account changes
    useEffect(() => {
        setSelectedCategory(null);
        setRestrictedBrands([]);
        setPrioritisedBrands([]);
    }, [mainAccountNumber]); // eslint-disable-line

    const onSubmitSearchForm = async (formData, actions) => {
        setLoading(true);
        setNoResults(false);
        setMainAccountNumber(formData.mainAccountNumber);
        setBabbageAccounts([]);

        try {
            const { data } = await client(GET_LIST, "babbage-accounts", {
                id: "babbage-accounts",
                filter: {
                    mainAccountNumber: formData.mainAccountNumber,
                    itemsPerPage: 10000,
                },
            });

            setNoResults(data.length === 0);
            setBabbageAccounts(formatDataForForm(data[0], categoryOptions));
        } catch (e) {
            console.error(e);
            toast.error(
                e?.error?.body?.hasOwnProperty("hydra:description")
                    ? e.error.body["hydra:description"]
                    : "There was an error submitting this form."
            );
        } finally {
            setLoading(false);
            actions.setSubmitting(false);
        }
    };

    const onUpdateAccount = async (formData, actions) => {
        const formattedFormData = formatDataForAPI(formData, categoryOptions);

        try {
            await client(UPDATE, "babbage-account-configuration-settings", {
                id: `/babbage-account-configuration-settings/${mainAccountNumber}`,
                data: formattedFormData,
            });

            const { data } = await client(GET_LIST, "babbage-accounts", {
                id: "babbage-accounts",
                filter: { mainAccountNumber, itemsPerPage: 10000 },
            });

            setNoResults(data.length === 0);
            setBabbageAccounts(formatDataForForm(data[0], categoryOptions));

            toast.success("Babbage account settings successfully updated");
        } catch (e) {
            console.error(e);
            toast.error(
                e?.error?.body?.hasOwnProperty("hydra:description")
                    ? e.error.body["hydra:description"]
                    : "There was an error updating this value."
            );
        } finally {
            actions.setSubmitting(false);
        }
    };

    const showForm = babbageAccounts.length > 0;
    const isLoadingForm =
        loading ||
        (showForm && categoriesLoading) ||
        (showForm && t3CategoriesLoading) ||
        (showForm && brandsLoading);

    return (
        <>
            <Helmet>
                <title>Babbage Accounts | Peracto</title>
            </Helmet>
            <Heading name="Babbage Accounts" />

            <div className="row">
                <div className="col">
                    <Form
                        autoComplete="on"
                        values={{ mainAccountNumber: "" }}
                        schema={schema}
                        showActionPanel={false}
                        onSubmit={onSubmitSearchForm}
                    >
                        <Group
                            key="babbage-account-number"
                            id="babbage-account-number"
                            name="Babbage Account Number"
                        >
                            <Input
                                name="mainAccountNumber"
                                label="Main Account Number"
                                testId="mainAccountNumber"
                                required
                            />
                            <button
                                className="btn btn-primary"
                                type="submit"
                                data-testid="submit"
                            >
                                Submit
                            </button>
                        </Group>
                    </Form>

                    {isLoadingForm ? (
                        <div className="flex items-center justify-center py-20">
                            <LoadingIcon />
                        </div>
                    ) : showForm ? (
                        <Group
                            key="babbage-account-settings"
                            id="babbage-account-settings"
                            name="Babbage Account Settings"
                        >
                            <Form
                                autoComplete="on"
                                values={{
                                    mainAccountNumber: babbageAccounts?.find(
                                        (val) => val.field === "mainAccountNumber"
                                    )?.value,
                                    restrictHaynesProAccess: config.restrictHaynesProAccess,
                                    hideOmnihub: config.hideOmnihub,
                                    excludeMenuItems: config.excludeMenuItems,
                                    primaryColour: config.themeColours?.primaryColour,
                                    secondaryColour: config.themeColours?.secondaryColour,
                                    tertiaryColour: config.themeColours?.tertiaryColour,
                                    customLogo: config.customLogo,
                                    showStrippedFooter: config.showStrippedFooter,
                                    isKeyAccount: config.isKeyAccount,
                                    categoryBrandRestrictions: config.categoryBrandRestrictions,
                                    categoryBrandPriorities: config.categoryBrandPriorities,
                                    showOffers: config.showOffers,
                                    vehicleAgePopup: config.vehicleAgePopup,
                                }}
                                showActionPanel={false}
                                onSubmit={onUpdateAccount}
                            >
                                <LogoSync setCustomLogo={config.setCustomLogo} />
                                <p className="alert alert-info mt-[5px] font-light">
                                    Updating settings for account:{" "}
                                    <span className="font-bold">
                                        {
                                            babbageAccounts?.find(
                                                (val) => val.field === "mainAccountNumber"
                                            )?.value
                                        }
                                    </span>
                                </p>

                                <BabbageAccountConfigurationFields {...config} />

                                <button
                                    className="btn btn-success w-fit mt-8"
                                    type="submit"
                                    data-testid="submit"
                                >
                                    Save
                                </button>
                            </Form>
                        </Group>
                    ) : noResults ? (
                        <div className="py-20 text-center">
                            No Babbage Accounts found for "{mainAccountNumber}"
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};
